
import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

const GoldInvestmentSection = () => {
  const isMobile = useIsMobile();

  return (
    <section className="relative w-full overflow-hidden py-12 md:py-16 lg:py-24">
      {/* Image Background */}
      <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
        <div 
          className="w-full h-full bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: "url('https://www.datocms-assets.com/70624/1721646753-choose-your-path-card.jpg?auto=compress%2Cformat&q=60&w=1920')"
          }}
        />
      </div>
      
      {/* Content overlay - Adjusted padding for mobile */}
      <div className="relative z-10 flex flex-col items-center justify-center w-full px-4 md:px-8 py-6 md:py-8 lg:py-12">
        <div className="max-w-4xl text-center">
          <h2 className={`${isMobile ? 'text-3xl' : 'text-4xl md:text-5xl lg:text-6xl'} font-serif text-white font-bold mb-4 md:mb-6 animate-fade-in mt-0 pt-0`}>
            <span className="text-patrimoine-gold">LIVRET GOLD</span> {isMobile ? <br /> : '- '}L'OR COMME VALEUR REFUGE
          </h2>
          
          <div className="mb-6 md:mb-8 mt-6 md:mt-8">
            {/* Gold coin animation - made responsive */}
            <svg width={isMobile ? "40" : "60"} height={isMobile ? "80" : "120"} viewBox="0 0 60 120" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <defs>
                <linearGradient id="Gradient1" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform="rotate(120)">
                  <stop offset="0%" stopColor="#BC8D29"></stop>
                  <stop offset="50%" stopColor="#BC8D29"></stop>
                  <stop offset="100%" stopColor="#BC8D29"></stop>
                </linearGradient>
              </defs>
              <filter id="blur">
                <feGaussianBlur in="SourceGraphic" stdDeviation="0.5"></feGaussianBlur>
              </filter>
              <mask id="circles">
                <g stroke="#FFFFFF" strokeWidth="3" fill="none">
                  <circle cx="30" cy="30" r="1">
                    <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0; 0.15; 0.2; 0.8; 0.85; 1" dur="6s" repeatCount="indefinite"></animate>
                  </circle>
                  <circle cx="30" cy="30" r="13.5">
                    <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0; 0.05; 0.1; 0.9; 0.95; 1" dur="6s" repeatCount="indefinite"></animate>
                  </circle>
                  <circle cx="30" cy="30" r="21">
                    <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0; 0.1; 0.15; 0.85; 0.9; 1" dur="6s" repeatCount="indefinite"></animate>
                  </circle>
                  <circle cx="30" cy="30" r="28.5">
                    <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0; 0.15; 0.2; 0.8; 0.85; 1" dur="6s" repeatCount="indefinite"></animate>
                  </circle>
                </g>
              </mask>
              <mask id="line">
                <line x1="30" y1="38" x2="30" y2="120" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round">
                  <animate attributeName="y1" values="38;38;90;142" dur="1s" repeatCount="indefinite"></animate>
                  <animate attributeName="y2" values="38;90;142;142" dur="1s" repeatCount="indefinite"></animate>
                  <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0; 0.2; 0.2; 0.8; 0.8; 1" dur="6s" repeatCount="indefinite"></animate>
                </line>
              </mask>
              <g filter="url(#blur)">
                <rect x="0" y="0" width="60" height="60" fill="url(#Gradient1)" mask="url(#circles)"></rect>
                <rect x="0" y="30" width="60" height="90" fill="url(#Gradient1)" mask="url(#line)"></rect>
              </g>
            </svg>
          </div>
          
          <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-3'} gap-4 md:gap-6 mb-6 md:mb-8`}>
            <div className="bg-black/60 backdrop-blur-sm border border-patrimoine-gold rounded-md p-4 md:p-6 text-white hover:scale-105 transition-transform duration-300">
              <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-patrimoine-gold mb-2`}>Pack Standard</h3>
              <p className={`${isMobile ? 'text-sm' : ''} mb-2`}>L'or conserve sa valeur face à l'inflation.</p>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-patrimoine-gold`}>8,12% brut/an (10k€ à 49 999€)</p>
            </div>
            
            <div className="bg-black/60 backdrop-blur-sm border border-patrimoine-gold rounded-md p-4 md:p-6 text-white hover:scale-105 transition-transform duration-300">
              <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-patrimoine-gold mb-2`}>Pack Premium</h3>
              <p className={`${isMobile ? 'text-sm' : ''} mb-2`}>Un actif tangible pour diversifier votre patrimoine.</p>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-patrimoine-gold`}>9,22% brut/an (50k€ à 199 999€)</p>
            </div>
            
            <div className="bg-black/60 backdrop-blur-sm border border-patrimoine-gold rounded-md p-4 md:p-6 text-white hover:scale-105 transition-transform duration-300">
              <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-patrimoine-gold mb-2`}>Pack Gold</h3>
              <p className={`${isMobile ? 'text-sm' : ''} mb-2`}>Liquidité garantie avec cotation transparente.</p>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-patrimoine-gold`}>11,09% brut/an (200k€ à 1M€)</p>
            </div>
          </div>
          
          <Link to="/bilan-patrimonial">
            <Button className="cta-button mt-4 animate-fade-in group" style={{ animationDelay: "0.4s" }}>
              Découvrir notre Livret Gold
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default GoldInvestmentSection;
